import request from 'services/request'
import _get from 'lodash/get'
import { stringify as qsStringifyQuery } from 'qs'
import { createDataUri, dataUriToBlob } from '../../data-uri'
import { createAuthorization } from '../auth'
import { APPLICATION_PDF } from './constants'
import { IMAGE_GIF, IMAGE_JPEG, IMAGE_PNG } from '../images/constants'

const ASSETS_ENDPOINT = 'v1/assets'

/**
 * Given a filename, returns the mime type.
 * @param {String} filename The name of the file.
 * @returns {String} The mime type of the given file.
 */
const getMimeTypeFromFilename = (filename) => {
  const extension = filename.split('.').pop()
  switch (extension) {
    case 'pdf':
      return APPLICATION_PDF
    case 'png':
      return IMAGE_PNG
    case 'jpg':
      return IMAGE_JPEG
    case 'jpeg':
      return IMAGE_JPEG
    case 'gif':
      return IMAGE_GIF
    default:
      return 'application/octet-stream'
  }
}

/**
 * Creates a new asset.
 * @param {Object} options The options object.
 * @param {Object} options.auth The authentication object.
 * @param {string} options.server The server URL.
 * @param {Object} options.body The asset data.
 * @returns {Promise<Object>} A promise that resolves to the created asset.
 */
export async function createAsset ({ auth, server, body }) {
  const mimeType = getMimeTypeFromFilename(body.originalFileName)
  const fileDataUri = createDataUri(body.fileBase64, mimeType)
  const fileBlob = dataUriToBlob(fileDataUri)
  const res = await request
    .post(`${server}/${ASSETS_ENDPOINT}`)
    .attach('assetFile', fileBlob, body.originalFileName)
    .set('authorization', createAuthorization(auth))
    .field({
      contentId: body.contentId,
      contentType: body.contentType,
      contentReferenceType: body.contentReferenceType,
      language: body.language,
      originalFileName: body.originalFileName,
    })
  return _get(res, 'body', {})
}

/**
 * Deletes an asset.
 * @param {Object} options The options object.
 * @param {Object} options.auth The authentication object.
 * @param {string} options.server The server URL.
 * @param {Object} options.body The asset data.
 * @param {string} options.body.uuid The UUID of the asset to delete.
 * @returns {Promise<Object>} A promise that resolves to the deleted asset.
 * @throws {Error} If the UUID is not provided.
 */
export async function deleteAsset (options = {}) {
  const { auth, server, body } = options
  const { uuid } = body
  if (!uuid) {
    throw new Error('The uuid option is required')
  }
  const res = await request
    .delete(`${server}/${ASSETS_ENDPOINT}/${uuid}`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
  return _get(res, 'body', {})
}

/**
 * Lists all assets.
 * @param {Object} options The options object.
 * @param {Object} options.auth The authentication object.
 * @param {string} options.server The server URL.
 * @param {Object} options.query The request query.
 * @returns {Promise<Object>} A promise that resolves to an array of assets.
 */
export async function listAssets (options = {}) {
  const { auth, server, query } = options
  const req = request
    .get(`${server}/${ASSETS_ENDPOINT}/list`)
  if (query) {
    req.query(qsStringifyQuery(query))
  }
  req.set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
  const res = await req
  return _get(res, 'body', {})
}
